import { useMemo } from 'react'
import { useProjektprognosEditingContext } from '../../context/ProjektprognosEditingContext'
import { usePeriodReportSnapshotData } from '../../fetchData/queries/usePeriodReportSnapshotData'
import { usePeriodReportSnapshots } from '../../fetchData/queries/usePeriodReportSnapshots'
import { useProjektprognosProjectInfo } from '../../fetchData/queries/useProjectInfo'
import { getReportSnapshotIds } from '../../utils/getReportSnapshotIds'
import { DatesInfo } from '../../../../interfaces/baseCalculation/DatesInfo'
import { InterestInfo } from '../../../../interfaces/baseCalculation/InterestInfo'

type ReportProjectAttributes = {
  datesInfo?: DatesInfo
  interestInfo?: InterestInfo
} | null

export const useProjectReportComparisonData = () => {
  const { selectedPeriodReport } = useProjektprognosEditingContext()

  const { data: periodReports, isSuccess: isPeriodReportsSuccess } =
    usePeriodReportSnapshots()
  const hasPeriodReports = (periodReports?.length ?? 0) > 0

  const { previousSnapshotId, currentSnapshotId } = useMemo(
    () => getReportSnapshotIds(selectedPeriodReport),
    [selectedPeriodReport]
  )

  const { previousSnapshot, currentSnapshot } = usePeriodReportSnapshotData(
    previousSnapshotId,
    currentSnapshotId
  )

  // Only fetch live data when: projectForecast exists, there is a previousSnapshot
  // to compare with, and there is no currentSnapshot (i.e. not viewing a closed report)
  const liveProjectInfo = useProjektprognosProjectInfo({
    enabled:
      isPeriodReportsSuccess &&
      hasPeriodReports &&
      !currentSnapshotId &&
      !!previousSnapshotId,
  })

  const previousProjectAttributes: ReportProjectAttributes =
    previousSnapshot.data?.projectAttributes || null

  const currentProjectAttributes: ReportProjectAttributes = currentSnapshotId
    ? currentSnapshot.data?.projectAttributes || null
    : liveProjectInfo.data
      ? {
          datesInfo: liveProjectInfo.data.datesInfo,
          interestInfo: liveProjectInfo.data.interestInfo,
        }
      : null

  const currentIsLoading = currentSnapshotId
    ? currentSnapshot.isLoading || currentSnapshot.isFetching
    : liveProjectInfo.isLoading || liveProjectInfo.isFetching

  const currentError = currentSnapshotId
    ? currentSnapshot.error
    : liveProjectInfo.error

  const isLoading =
    previousSnapshot.isLoading ||
    previousSnapshot.isFetching ||
    currentIsLoading

  const error = previousSnapshot.error || currentError

  return {
    previousProjectAttributes,
    currentProjectAttributes,
    isLoading,
    error,
  }
}
